import React from 'react'
import Helmet from 'react-helmet'
import styled from 'styled-components'
import { Flex, Box } from 'rebass'
import { rem } from 'polished'
import createMeta from '../../utils/meta-creator'
import renderWidgets from '../../../lib/widget-renderer'
import formatEventDateRange from '../../../lib/event-date-range-formatter'

import { media, SmallScreenOnly } from '../../utils/style'
import typography from '../../utils/typography'

import Tags from '../Tags'
import ContentArticleHeader from '../ContentArticleHeader'
import ContentArticleSocialShare from '../ContentArticleSocialShare'
import ContentArticleNextPrevPosts from '../ContentArticleNextPrevPosts'

const Wrapper = styled(Box)`
  background-color: ${(props) => props.theme.colors.blue_light};
  padding-top: 1px;
  padding-bottom: 1px;
`

const ContentFrame = styled(Box)`
  background-color: white;
  width: 100%;
  max-width: ${(props) => props.theme.content.max.width};
  margin-left: auto;
  margin-right: auto;
`

const Content = styled(Flex)`
  img {
    margin: ${typography.rhythm(0.5)} 0;
  }
`

const ArticleBody = styled(Box)`
  & > div:first-child > p:first-child {
    font-weight: 600;
    font-size: ${rem(18)};
    ${media.LargeScreenUp`
      font-size: ${rem(20)};
    `};
    color: ${(props) => props.theme.colors.grey_light};
  }
`

const TagFrame = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: rgba(238, 244, 249, 1);
  ${media.GiantScreenUp`
    align-items: left;
    justify-content: left;
  `};
`

const Share = styled(Box)`
  h5 {
    margin-top: ${typography.rhythm(0.2)};
    text-transform: uppercase;
  }
`

const NextPrevFrame = styled(Box)`
  width: 100%;
  max-width: ${(props) => props.theme.content.max.width};
  margin-left: auto;
  margin-right: auto;
`

const normaliseArticleToNextPrevious = (article) => {
  if (!article) return null
  return {
    shortHeadline: article.shortHeadline,
    category: article.tags && article.tags.length > 0 ? article.tags[0] : null,
    path: '/blog/' + article.slug,
    image:
      article.images && article.images.length > 0 ? article.images[0] : null
  }
}

const ArticleComponent = ({ article, locations }) => {
  const shareURL = typeof window !== 'undefined' ? window.location.href : ''
  const hashtag = ['citygate']
  return (
    <div>
      <Helmet title={article.headline} meta={createMeta(article)} />
      <Wrapper>
        <ContentFrame mt={[0, 0, 0, 50]} mb={[30]} as="article">
          <ContentArticleHeader
            title={article.headline}
            colour={article.headingColour}
            category={article.tags.length > 0 && article.tags[0]}
            author={article.author}
            bg={article.images[0]}
            location={{
              label: article.eventLocation,
              url: article.locationUrl
            }}
            ctaLabel="Register now"
            articleId={article.id}
            isEvent={article.articleType === 'event'}
            {...(article.articleType !== 'event' && {
              date: article.displayDate
            })}
            {...(article.articleType === 'event' &&
              article.eventStartDate &&
              article.eventEndDate && {
                subtitle: formatEventDateRange({
                  start: article.eventStartDate,
                  end: article.eventEndDate
                })
              })}
            showCalendarLink={article.showCalendarLink}
            showRegisterLink={article.showRegisterLink}
          />

          <Content flexWrap="wrap" px={[10, 75]} py={[10, 75]}>
            <ArticleBody width={[1, 9 / 12]} px={['6%', 0]} order={2}>
              {renderWidgets({
                widgets: article.body,
                pageType: 'article',
                locations
              })}
            </ArticleBody>
            <Share
              width={[1, 1 / 12]}
              order={1}
              mr={[0, `${(0.5 / 12) * 100}%`]}
            >
              <ContentArticleSocialShare
                url={shareURL}
                title={article.headline}
                hashtag={hashtag}
                position={'top'}
              />
            </Share>
          </Content>

          <SmallScreenOnly>
            <ContentArticleSocialShare
              url={shareURL}
              title={article.headline}
              hashtag={hashtag}
              position={'bottom'}
            />
          </SmallScreenOnly>
          {article.tags && article.tags.length > 0 && (
            <TagFrame p={30}>
              <Tags list={article.tags} />
            </TagFrame>
          )}
        </ContentFrame>

        <NextPrevFrame>
          <ContentArticleNextPrevPosts
            prev={normaliseArticleToNextPrevious(article.previous)}
            next={normaliseArticleToNextPrevious(article.next)}
          />
        </NextPrevFrame>
      </Wrapper>
    </div>
  )
}

export default ArticleComponent
